/**
 * Appointment form component for scheduling new appointments
 */

import React from 'react';
import { Event } from '@mui/icons-material';
import { DateField } from '../../dropdown';
import { Ticket } from '../../types/ticketTypes';
import { getAvailableTechnicians } from '../utils/ticketUtils';
import { AppointmentsHandlers } from '../hooks/useAppointments';

export interface AppointmentFormProps {
  ticket: Ticket;
  tickets: Ticket[];
  appointmentsHandlers: AppointmentsHandlers;
  isVisible: boolean;
  onClose: () => void;
}

export const AppointmentForm: React.FC<AppointmentFormProps> = ({
  ticket,
  tickets,
  appointmentsHandlers,
  isVisible,
  onClose
}) => {
  const {
    appointmentDate,
    setAppointmentDate,
    appointmentTime,
    setAppointmentTime,
    appointmentTask,
    setAppointmentTask,
    appointmentOwner,
    setAppointmentOwner,
    editingAppointmentId,
    setEditingAppointmentId,
    handleAddAppointment,
    handleUpdateAppointment
  } = appointmentsHandlers;

  const isEditMode = editingAppointmentId !== null;

  const handleSubmit = () => {
    if (isEditMode) {
      handleUpdateAppointment(ticket, editingAppointmentId);
    } else {
      handleAddAppointment(ticket);
    }
    onClose();
  };

  const handleCancel = () => {
    if (isEditMode) {
      setEditingAppointmentId(null);
      // Clear form
      setAppointmentDate('');
      setAppointmentTime('');
      setAppointmentTask('');
      setAppointmentOwner('');
    }
    onClose();
  };

  if (!isVisible) {
    return null;
  }

  return (
    <div className="ticket-details-section">
      <div className="section-header">
        <h4>{isEditMode ? 'Edit Appointment' : 'Schedule Appointment'}</h4>
        <button
          type="button"
          className="icon-btn"
          onClick={handleCancel}
          title="Close"
        >
          ×
        </button>
      </div>
      <div className="appointment-form-content-box">
        <div className="appointment-form-row">
          <div className="form-group">
            <DateField
              label="Date"
              value={appointmentDate || null}
              onChange={(iso) => setAppointmentDate(iso ?? '')}
            />
          </div>
          <div className="form-group">
            <label htmlFor={`appointment-time-${ticket.id}`}>Time:</label>
            <input
              type="time"
              id={`appointment-time-${ticket.id}`}
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor={`appointment-task-${ticket.id}`}>Task:</label>
          <textarea
            id={`appointment-task-${ticket.id}`}
            value={appointmentTask}
            onChange={(e) => setAppointmentTask(e.target.value)}
            placeholder="Describe the appointment task..."
            rows={2}
          />
        </div>
        <div className="form-group">
          <label htmlFor={`appointment-owner-${ticket.id}`}>Assigned To:</label>
          <select
            id={`appointment-owner-${ticket.id}`}
            value={appointmentOwner}
            onChange={(e) => setAppointmentOwner(e.target.value)}
          >
            <option value="">{ticket.assignedTo} (Default)</option>
            {getAvailableTechnicians(tickets)
              .filter((tech: string) => tech !== ticket.assignedTo)
              .map((tech: string) => (
                <option key={tech} value={tech}>{tech}</option>
              ))
            }
          </select>
        </div>
        <div className="button-group">
          <button 
            type="button"
            className="add-btn appointment-btn"
            onClick={handleSubmit}
            disabled={!appointmentDate || !appointmentTime || !appointmentTask.trim()}
          >
            <Event style={{ fontSize: '16px', marginRight: '4px' }} />
            {isEditMode ? 'Update Appointment' : 'Schedule Appointment'}
          </button>
          <button 
            type="button"
            className="cancel-btn"
            onClick={handleCancel}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};